import { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { auth, db, storage } from "../../../firebase";
import {
  DEFAULT_PARTNER_CONTRACT,
  isValidContractEmail,
  makeContractStorageFileName,
  normalizeContractEmail,
  normalizePartnerContract,
  PARTNER_CONTRACT_DOCUMENT_ID,
  PARTNER_CONTRACT_SLOT,
  validatePartnerContractFile,
} from "../../../services/partnerContractLink";

const emptyForm = {
  title: "",
  url: "",
  type: "url",
  order: 10,
  isActive: true,
  fileName: "",
  storagePath: "",
};

function PartnerContractLinkAdmin() {
  const [settings, setSettings] = useState(DEFAULT_PARTNER_CONTRACT);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    getDoc(doc(db, "footerLinks", PARTNER_CONTRACT_DOCUMENT_ID))
      .then((snapshot) => {
        if (!active) return;
        setSettings(normalizePartnerContract(
          snapshot.exists() ? snapshot.data() : DEFAULT_PARTNER_CONTRACT,
        ));
      })
      .catch((loadError) => {
        console.error("Partner contract link load error:", loadError);
        if (active) setError("Не вдалося завантажити налаштування договору.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const selectFile = (file) => {
    setFeedback("");
    setError("");

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const validationError = validatePartnerContractFile(file);
    if (validationError) {
      setSelectedFile(null);
      setFileInputKey((value) => value + 1);
      setError(validationError);
      return;
    }

    setSelectedFile(file);
  };

  const uploadContractFile = async (file) => {
    const safeName = makeContractStorageFileName(file.name);
    const storagePath = `footer-files/partner-contract/${Date.now()}-${safeName}`;
    const fileRef = ref(storage, storagePath);

    await uploadBytes(
      fileRef,
      file,
      file.type ? { contentType: file.type } : undefined,
    );

    return {
      url: await getDownloadURL(fileRef),
      fileName: file.name,
      storagePath,
    };
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setFeedback("");
    setError("");

    const email = normalizeContractEmail(settings.email);
    if (settings.type === "email" && !isValidContractEmail(email)) {
      setError("Вкажіть коректну електронну адресу.");
      return;
    }

    if (settings.type === "file" && !selectedFile && !settings.url) {
      setError("Оберіть файл договору.");
      return;
    }

    if (selectedFile) {
      const validationError = validatePartnerContractFile(selectedFile);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setSaving(true);
    const previousStoragePath = settings.storagePath;
    let uploadedStoragePath = "";

    try {
      let nextFile = {
        url: settings.url,
        fileName: settings.fileName,
        storagePath: settings.storagePath,
      };

      if (settings.type === "file" && selectedFile) {
        nextFile = await uploadContractFile(selectedFile);
        uploadedStoragePath = nextFile.storagePath;
      }

      if (settings.type === "email") {
        nextFile = { url: "", fileName: "", storagePath: "" };
      }

      const payload = {
        slot: PARTNER_CONTRACT_SLOT,
        title: "Хочете стати нашим партнером? Замовте договір!",
        type: settings.type,
        email,
        ...nextFile,
        order: 0,
        isActive: true,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null,
      };

      await setDoc(
        doc(db, "footerLinks", PARTNER_CONTRACT_DOCUMENT_ID),
        payload,
        { merge: true },
      );

      if (
        previousStoragePath &&
        previousStoragePath !== nextFile.storagePath
      ) {
        deleteObject(ref(storage, previousStoragePath)).catch((deleteError) => {
          console.warn("Partner contract old file delete warning:", deleteError);
        });
      }

      setSettings(normalizePartnerContract(payload));
      setSelectedFile(null);
      setFileInputKey((value) => value + 1);
      setFeedback("Посилання договору збережено.");
    } catch (saveError) {
      console.error("Partner contract link save error:", saveError);

      if (uploadedStoragePath) {
        deleteObject(ref(storage, uploadedStoragePath)).catch(() => {});
      }

      setError("Не вдалося зберегти посилання договору.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{ marginBottom: "36px" }}>
      <h2>Посилання «Замовте договір»</h2>

      {loading ? (
        <p>Завантаження...</p>
      ) : (
        <form onSubmit={saveSettings}>
          <div style={{ marginBottom: "15px" }}>
            <label htmlFor="partner-contract-type">Тип посилання</label>
            <select
              id="partner-contract-type"
              value={settings.type}
              disabled={saving}
              onChange={(event) => {
                setSettings((current) => ({
                  ...current,
                  type: event.target.value,
                }));
                setFeedback("");
                setError("");
              }}
              style={{ display: "block", width: "100%", padding: "10px" }}
            >
              <option value="email">Електронна пошта</option>
              <option value="file">Файл договору</option>
            </select>
          </div>

          {settings.type === "email" && (
            <div style={{ marginBottom: "15px" }}>
              <label htmlFor="partner-contract-email">Email</label>
              <input
                id="partner-contract-email"
                type="email"
                value={settings.email}
                disabled={saving}
                onChange={(event) => setSettings((current) => ({
                  ...current,
                  email: event.target.value,
                }))}
                placeholder="name@example.com"
                style={{ display: "block", width: "100%", padding: "10px" }}
              />
            </div>
          )}

          {settings.type === "file" && (
            <div style={{ marginBottom: "15px" }}>
              <label htmlFor="partner-contract-file">Файл договору</label>
              <input
                key={fileInputKey}
                id="partner-contract-file"
                type="file"
                accept=".pdf,.doc,.docx,.odt,.rtf"
                disabled={saving}
                onChange={(event) => selectFile(event.target.files?.[0] || null)}
                style={{ display: "block", marginTop: "8px" }}
              />

              {(selectedFile || settings.fileName) && (
                <p style={{ marginTop: "8px" }}>
                  Файл: <b>{selectedFile?.name || settings.fileName}</b>
                </p>
              )}

              {settings.url && !selectedFile && (
                <a
                  href={settings.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Відкрити поточний файл
                </a>
              )}
            </div>
          )}

          <button type="submit" disabled={saving}>
            {saving ? "Збереження..." : "Зберегти"}
          </button>

          {feedback && <p style={{ color: "#166534" }}>{feedback}</p>}
          {error && <p style={{ color: "#b91c1c" }} role="alert">{error}</p>}
        </form>
      )}
    </section>
  );
}

export default function FooterLinksAdmin() {
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadLinks = async () => {
    const q = query(collection(db, "footerLinks"), orderBy("order", "asc"));
    const snap = await getDocs(q);

    const list = snap.docs
      .filter((item) => item.id !== PARTNER_CONTRACT_DOCUMENT_ID)
      .map((item) => ({
        id: item.id,
        ...item.data(),
      }));

    setLinks(list);
  };

  useEffect(() => {
    loadLinks();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setSelectedFile(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setSelectedFile(null);

    setForm({
      title: item.title || "",
      url: item.url || "",
      type: item.type || "url",
      order: item.order || 10,
      isActive: item.isActive !== false,
      fileName: item.fileName || "",
      storagePath: item.storagePath || "",
    });
  };

  const uploadFooterFile = async (file) => {
    const safeName = file.name.replace(/[^\wа-яА-ЯіїєґІЇЄҐ.-]+/g, "_");
    const storagePath = `footer-files/${Date.now()}-${safeName}`;

    const fileRef = ref(storage, storagePath);

    await uploadBytes(fileRef, file);

    const downloadUrl = await getDownloadURL(fileRef);

    return {
      url: downloadUrl,
      fileName: file.name,
      storagePath,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      alert("Вкажіть назву посилання");
      return;
    }

    if (form.type === "url" && !form.url.trim()) {
      alert("Вкажіть посилання");
      return;
    }

    if (form.type === "file" && !selectedFile && !form.url) {
      alert("Завантажте файл");
      return;
    }

    setLoading(true);

    try {
      let finalUrl = form.url;
      let finalFileName = form.fileName || "";
      let finalStoragePath = form.storagePath || "";

      if (form.type === "file" && selectedFile) {
        const uploaded = await uploadFooterFile(selectedFile);

        finalUrl = uploaded.url;
        finalFileName = uploaded.fileName;
        finalStoragePath = uploaded.storagePath;

        // Якщо редагуємо старий файл — видаляємо попередній зі Storage
        if (editingId && form.storagePath) {
          try {
            await deleteObject(ref(storage, form.storagePath));
          } catch (error) {
            console.warn("Old file delete warning:", error);
          }
        }
      }

      const payload = {
        title: form.title.trim(),
        url: finalUrl.trim(),
        type: form.type,
        order: Number(form.order) || 0,
        isActive: Boolean(form.isActive),
        fileName: finalFileName,
        storagePath: finalStoragePath,
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "footerLinks", editingId), payload);
      } else {
        await addDoc(collection(db, "footerLinks"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      await loadLinks();
      resetForm();
    } catch (error) {
      console.error("Footer link save error:", error);
      alert("Не вдалося зберегти посилання");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    const ok = window.confirm(`Видалити "${item.title}"?`);
    if (!ok) return;

    setLoading(true);

    try {
      if (item.storagePath) {
        try {
          await deleteObject(ref(storage, item.storagePath));
        } catch (error) {
          console.warn("File delete warning:", error);
        }
      }

      await deleteDoc(doc(db, "footerLinks", item.id));

      await loadLinks();

      if (editingId === item.id) {
        resetForm();
      }
    } catch (error) {
      console.error("Footer link delete error:", error);
      alert("Не вдалося видалити посилання");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: "900px", padding: "30px" }}>
      <PartnerContractLinkAdmin />

      <h2>Посилання у футері</h2>

      <form onSubmit={handleSubmit} style={{ marginBottom: "30px" }}>
        <div style={{ marginBottom: "15px" }}>
          <label>Назва</label>
          <input
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Наприклад: Каталог"
            style={{ display: "block", width: "100%", padding: "10px" }}
          />
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label>Тип</label>
          <select
            name="type"
            value={form.type}
            onChange={handleChange}
            style={{ display: "block", width: "100%", padding: "10px" }}
          >
            <option value="url">Зовнішнє посилання</option>
            <option value="file">Файл</option>
          </select>
        </div>

        {form.type === "url" && (
          <div style={{ marginBottom: "15px" }}>
            <label>Посилання</label>
            <input
              name="url"
              value={form.url}
              onChange={handleChange}
              placeholder="https://..."
              style={{ display: "block", width: "100%", padding: "10px" }}
            />
          </div>
        )}

        {form.type === "file" && (
          <div style={{ marginBottom: "15px" }}>
            <label>Файл</label>
            <input
              type="file"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={{ display: "block", marginTop: "8px" }}
            />

            {form.fileName && (
              <p style={{ marginTop: "8px" }}>
                Поточний файл: <b>{form.fileName}</b>
              </p>
            )}

            {form.url && (
              <a href={form.url} target="_blank" rel="noopener noreferrer">
                Переглянути поточний файл
              </a>
            )}
          </div>
        )}

        <div style={{ marginBottom: "15px" }}>
          <label>Порядок</label>
          <input
            name="order"
            type="number"
            value={form.order}
            onChange={handleChange}
            style={{ display: "block", width: "100%", padding: "10px" }}
          />
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label>
            <input
              name="isActive"
              type="checkbox"
              checked={form.isActive}
              onChange={handleChange}
            />{" "}
            Активне посилання
          </label>
        </div>

        <button type="submit" disabled={loading}>
          {editingId ? "Оновити" : "Додати"}
        </button>

        {editingId && (
          <button
            type="button"
            onClick={resetForm}
            disabled={loading}
            style={{ marginLeft: "10px" }}
          >
            Скасувати
          </button>
        )}
      </form>

      <h3>Поточні посилання</h3>

      <div style={{ display: "grid", gap: "12px" }}>
        {links.map((item) => (
          <div
            key={item.id}
            style={{
              border: "1px solid #ddd",
              padding: "15px",
              borderRadius: "8px",
            }}
          >
            <div>
              <b>{item.title}</b>{" "}
              {!item.isActive && (
                <span style={{ color: "red" }}>(вимкнено)</span>
              )}
            </div>

            <div>Тип: {item.type === "file" ? "Файл" : "Посилання"}</div>
            <div>Порядок: {item.order}</div>

            {item.fileName && <div>Файл: {item.fileName}</div>}

            <a href={item.url} target="_blank" rel="noopener noreferrer">
              Відкрити
            </a>

            <div style={{ marginTop: "10px" }}>
              <button type="button" onClick={() => handleEdit(item)}>
                Редагувати
              </button>

              <button
                type="button"
                onClick={() => handleDelete(item)}
                style={{ marginLeft: "10px" }}
              >
                Видалити
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
